
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Appointment, ReschedulePolicy, DateTimeSlot } from '@/types/dashboard';
import RescheduleForm from './reschedule/RescheduleForm';
import SuccessMessage from './reschedule/SuccessMessage';

interface RescheduleModalProps {
  appointment: Appointment;
  userRole: 'psychologist' | 'patient';
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (appointmentId: string, reason: string, newDateTime: Date) => Promise<void>;
  policy: ReschedulePolicy;
  availableSlots: DateTimeSlot[];
  onFetchAvailableSlots: (date: Date) => Promise<void>;
}

/**
 * Modal para processar reagendamentos de consulta
 * Permite selecionar nova data/hora e solicita motivo do reagendamento
 */
const RescheduleModal: React.FC<RescheduleModalProps> = ({
  appointment,
  userRole,
  isOpen,
  onClose,
  onConfirm,
  policy,
  availableSlots,
  onFetchAvailableSlots
}) => {
  // Estados locais
  const [reason, setReason] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Calcula o tempo restante até a consulta em horas
  const hoursUntilAppointment = appointment.scheduledAt 
    ? Math.floor((new Date(appointment.scheduledAt).getTime() - new Date().getTime()) / (1000 * 60 * 60))
    : 0;

  // Verifica se pode reagendar com base na política
  const canReschedule = hoursUntilAppointment >= policy.minHoursBeforeAppointment;

  // Função para lidar com mudança de data
  const handleDateChange = async (date: string) => {
    setSelectedDate(date);
    setSelectedTime('');
    if (date) {
      await onFetchAvailableSlots(new Date(date));
    }
  };

  // Função para lidar com o reagendamento
  const handleConfirm = async () => {
    if (!reason.trim()) {
      setError('Por favor, informe o motivo do reagendamento');
      return;
    }

    if (!selectedDate || !selectedTime) {
      setError('Por favor, selecione a nova data e horário');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const newDateTime = new Date(`${selectedDate}T${selectedTime}`);
      await onConfirm(appointment.id, reason, newDateTime);
      setSuccess(true);
      // Após 2 segundos, fecha o modal
      setTimeout(() => {
        setSuccess(false);
        onClose();
        setReason('');
        setSelectedDate('');
        setSelectedTime('');
      }, 2000);
    } catch (err) {
      setError('Ocorreu um erro ao solicitar o reagendamento. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Função para fechar o modal e resetar estados
  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      setReason('');
      setSelectedDate('');
      setSelectedTime('');
      setError(null);
      setSuccess(false);
    }
  };

  // Formatar data da consulta
  const formatDateTime = (dateTimeString?: string | Date) => {
    if (!dateTimeString) return 'Data não disponível';
    const date = new Date(dateTimeString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reagendar Consulta</DialogTitle>
          <DialogDescription>
            Consulta atual: {formatDateTime(appointment.scheduledAt)}
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <SuccessMessage userRole={userRole} />
        ) : (
          <>
            <RescheduleForm
              reason={reason}
              selectedDate={selectedDate}
              selectedTime={selectedTime}
              availableSlots={availableSlots}
              policy={policy}
              hoursUntilAppointment={hoursUntilAppointment}
              canReschedule={canReschedule}
              isSubmitting={isSubmitting}
              error={error}
              onReasonChange={setReason}
              onDateChange={handleDateChange}
              onTimeChange={setSelectedTime}
            />

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button 
                onClick={handleConfirm} 
                disabled={isSubmitting || !canReschedule}
              >
                {isSubmitting ? 'Processando...' : 'Solicitar Reagendamento'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RescheduleModal;
